'use client';

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Translations, Locale, Currency } from '@/i18n';
import { defaultTranslations, detectLocale, allLocales, localeCurrency } from '@/i18n';

interface LanguageContextValue {
  locale: Locale;
  currency: Currency;
  t: Translations;
  activeLocales: Locale[];
  hydrated: boolean;
  setLocale: (locale: Locale) => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  locale: 'da',
  currency: 'dkk',
  t: defaultTranslations.da,
  activeLocales: allLocales,
  hydrated: false,
  setLocale: () => {},
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('da');
  const [activeLocales, setActiveLocales] = useState<Locale[]>(allLocales);
  const [translations, setTranslations] = useState(defaultTranslations);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadTranslations() {
      try {
        const res = await fetch('/api/translations');
        if (!res.ok) throw new Error('Failed to load translations');
        const data = await res.json() as {
          activeLocales?: Locale[];
          translations?: Partial<Record<Locale, Translations>>;
        };

        if (!mounted) return;

        const allowed = (data.activeLocales ?? allLocales).filter(
          (l): l is Locale => typeof l === 'string' && allLocales.includes(l as Locale),
        );
        const safeActive: Locale[] = allowed.length > 0 ? allowed : ['da'];
        const merged = {
          ...defaultTranslations,
          ...data.translations,
        } as Record<Locale, Translations>;

        setActiveLocales(safeActive);
        setTranslations(merged);

        const detected = detectLocale(safeActive);
        setLocaleState(detected);
        document.documentElement.lang = detected;
      } catch {
        const detected = detectLocale(allLocales);
        if (!mounted) return;
        setLocaleState(detected);
        document.documentElement.lang = detected;
      } finally {
        if (mounted) setHydrated(true);
      }
    }

    loadTranslations();

    return () => {
      mounted = false;
    };
  }, []);

  const setLocale = useCallback((newLocale: Locale) => {
    if (!activeLocales.includes(newLocale)) return;
    setLocaleState(newLocale);
    localStorage.setItem('klonkaffe-locale', newLocale);
    document.documentElement.lang = newLocale;
  }, [activeLocales]);

  const t = translations[locale] ?? translations.da;
  const currency = localeCurrency[locale];

  return (
    <LanguageContext.Provider value={{ locale, currency, t, activeLocales, hydrated, setLocale }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  return useContext(LanguageContext);
}

export function useLocale() {
  return useContext(LanguageContext).locale;
}
